import { FC, useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { FORM_ERROR } from 'final-form'

// components
import EmailPasswordForm, {
  EmailPasswordFormProps,
} from 'components/login/LoginForm/EmailPasswordForm'

// utils
import AuthService from 'services/authentication'
import { useCurrentUser } from 'hooks'
import { showError } from 'helpers/message'

// constants
import { REAUTHENTICATE } from 'app/MissionControlStudio/apollo/auth/mutations'
import {
  REAUTH_MAX_ATTEMPTS_REACHED_ERROR_MESSAGE,
  SESSION_NOT_FOUND_ERROR_MESSAGE,
} from 'constants/message'
import { LOGIN_URL } from 'constants/route'

// types
import type { ReauthModalOptions } from 'contexts/reducers/ui'
import { AuthChallengeType, type MutationError } from 'types/graphql'
import { getMutationErrorMessage } from 'helpers/graphql'

const DEFAULT_ERROR_MESSAGE = 'Failed to re-authenticate'

const shouldMoveToLoginPage = (errorMessage: string) =>
  [
    SESSION_NOT_FOUND_ERROR_MESSAGE,
    REAUTH_MAX_ATTEMPTS_REACHED_ERROR_MESSAGE,
  ].some(errorText => errorMessage.includes(errorText))

const ReauthModalPasswordStep: FC<{
  onComplete: ReauthModalOptions['onComplete']
  onClose: () => void
  setAuthChallengeType: (type: AuthChallengeType) => void
}> = ({ onComplete, onClose, setAuthChallengeType }) => {
  const [reauthenticate] = useMutation(REAUTHENTICATE)
  const { currentUser } = useCurrentUser()

  const onSubmit = useCallback<EmailPasswordFormProps['onSubmit']>(
    async values => {
      const { password } = values
      const { username } = currentUser ?? {}

      const encodedDeviceFingerprint = AuthService.getUserContextData(username)

      try {
        const { data, errors } = await reauthenticate({
          variables: {
            input: {
              password,
              ...(encodedDeviceFingerprint && { encodedDeviceFingerprint }),
            },
          },
        })

        const { session } = data?.reauthenticate ?? {}
        const { reauthInfo } = session ?? {}

        if (!session) {
          const errorMessage =
            getMutationErrorMessage(errors as MutationError[]) ??
            DEFAULT_ERROR_MESSAGE

          if (shouldMoveToLoginPage(errorMessage)) {
            showError(errorMessage)
            // Moving a user to the login page after a short delay so that the user can read the error message
            setTimeout(() => {
              window.location.href = LOGIN_URL
            }, 2000)
          }

          return { [FORM_ERROR]: errorMessage }
        }

        // Go to MFA step if challengeName is present
        if (reauthInfo?.challengeName) {
          setAuthChallengeType(reauthInfo.challengeName)
          return null
        }

        // Otherwise, mark reauth as complete
        onComplete()
        onClose()
      } catch (error) {
        return {
          [FORM_ERROR]: (error as Error)?.message || DEFAULT_ERROR_MESSAGE,
        }
      }

      return null
    },
    [currentUser, reauthenticate, onComplete, onClose, setAuthChallengeType]
  )

  return (
    <EmailPasswordForm
      showEmailField={false}
      onSubmit={onSubmit}
      autoFocusOnPassword
      submitButtonText='Re-authenticate'
    />
  )
}

export default ReauthModalPasswordStep
